import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css'; // Pastikan CSS ini diimpor!
import L from 'leaflet'; // Untuk perbaikan ikon marker default dan fungsi L.icon

// PERBAIKAN PENTING UNTUK IKON MARKER DEFAULT LEAFLET DI REACT
// Tanpa ini, ikon marker default (untuk titik) mungkin tidak muncul
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// Sesuaikan pusat awal peta dan zoom
const initialCenter = [-5.45, 105.26]; // Contoh koordinat di Lampung
const initialZoom = 12;

function MapPage() {
  const [geoJsonLayers, setGeoJsonLayers] = useState({});
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const mapRef = useRef(null); // Ref untuk mengakses instance peta

  useEffect(() => {
    const loadGeoJsonFile = async (fileName, layerName) => {
      try {
        const response = await fetch(`/${fileName}`); // Asumsi file di folder public
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setGeoJsonLayers(prev => ({ ...prev, [layerName]: data }));
      } catch (error) {
        console.error(`Error loading ${fileName}:`, error);
      }
    };

    // Muat semua file GeoJSON Anda yang sudah diekspor dari QGIS
    // PASTIKAN NAMA FILE DI SINI SESUAI DENGAN NAMA FILE YANG ANDA EKSPOR
    loadGeoJsonFile('status_area.geojson', 'status');
    //loadGeoJsonFile('lebung_area.geojson', 'lebung');
    //loadGeoJsonFile('jalan_utama.geojson', 'jalanUtama');
    //loadGeoJsonFile('pipa_gas.geojson', 'pipaGas');
    //loadGeoJsonFile('pos_keamanan.geojson', 'posKeamanan');

  }, []);

  // Fungsi untuk menentukan gaya poligon 'status' berdasarkan properti 'status_tanaman'
  const getStatusStyle = (feature) => {
    const status = feature.properties.status_tanaman; // <-- PASTIKAN NAMA PROPERTI INI SESUAI DENGAN KOLOM DI GEOJSON ANDA
    let color;
    switch (status) {
      case 'On Std': color = 'green'; break;
      case 'Under Std': color = 'orange'; break;
      case 'Over Std': color = 'blue'; break;
      default: color = 'gray'; // Default jika status tidak dikenali
    }
    return { color: color, weight: 2, opacity: 0.8, fillOpacity: 0.5 };
  };

  // Fungsi untuk menentukan gaya poligon 'lebung'
  const getLebungStyle = { color: '#ADD8E6', weight: 1, opacity: 0.8, fillOpacity: 0.6 }; // Biru muda

  // Fungsi untuk menentukan gaya garis 'jalanUtama'
  const getJalanUtamaStyle = { color: '#8B4513', weight: 4, opacity: 0.9 }; // Coklat

  // Fungsi untuk menentukan gaya garis 'pipaGas'
  const getPipaGasStyle = { color: 'red', weight: 2, opacity: 0.7, dashArray: '5, 5' }; // Merah putus-putus

  // Fungsi untuk menentukan gaya titik 'posKeamanan'
  const getPosKeamananStyle = (feature, latlng) => {
    // Anda bisa menggunakan ikon kustom dari URL atau file lokal
    const customIcon = L.icon({
      iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png', // Contoh: ikon merah
      iconSize: [25, 41], // ukuran ikon
      iconAnchor: [12, 41], // titik jangkar ikon
      popupAnchor: [1, -34], // titik popup
      shadowSize: [41, 41] // ukuran bayangan
    });
    return L.marker(latlng, { icon: customIcon });
  };

  // Fungsi yang dijalankan untuk setiap fitur GeoJSON saat diklik
  const onEachFeature = (feature, layer) => {
    if (feature.properties) {
      layer.on('click', (e) => {
        setSelectedFeature(feature.properties);
        setModalOpen(true);
      });
    }
  };

  // Efek untuk menyesuaikan zoom peta agar semua layer terlihat (opsional)
  useEffect(() => {
    if (mapRef.current && Object.keys(geoJsonLayers).length > 0) {
      let bounds = new L.LatLngBounds([]);
      Object.values(geoJsonLayers).forEach(data => {
        if (data && data.features) {
          data.features.forEach(feature => {
            if (feature.geometry && feature.geometry.coordinates) {
              L.geoJson(feature).eachLayer(layer => {
                if (layer.getBounds) { // Untuk poligon/garis
                  bounds.extend(layer.getBounds());
                } else if (layer.getLatLng) { // Untuk titik
                  bounds.extend(layer.getLatLng());
                }
              });
            }
          });
        }
      });
      if (bounds.isValid()) {
        mapRef.current.fitBounds(bounds);
      }
    }
  }, [geoJsonLayers]);


  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      {/* MapContainer adalah komponen utama dari react-leaflet */}
      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        style={{ height: '600px', width: '100%' }} // Sesuaikan tinggi dan lebar peta
        ref={mapRef}
      >
        {/* TileLayer untuk peta dasar (OpenStreetMap) */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Tambahkan setiap layer GeoJSON */}
        {geoJsonLayers.status && (
          <GeoJSON data={geoJsonLayers.status} style={getStatusStyle} onEachFeature={onEachFeature} />
        )}
        {geoJsonLayers.lebung && (
          <GeoJSON data={geoJsonLayers.lebung} style={getLebungStyle} onEachFeature={onEachFeature} />
        )}
        {geoJsonLayers.jalanUtama && (
          <GeoJSON data={geoJsonLayers.jalanUtama} style={getJalanUtamaStyle} onEachFeature={onEachFeature} />
        )}
        {geoJsonLayers.pipaGas && (
          <GeoJSON data={geoJsonLayers.pipaGas} style={getPipaGasStyle} onEachFeature={onEachFeature} />
        )}
        {geoJsonLayers.posKeamanan && (
          <GeoJSON data={geoJsonLayers.posKeamanan} pointToLayer={getPosKeamananStyle} onEachFeature={onEachFeature} />
        )}
      </MapContainer>

      {/* Modal untuk menampilkan informasi detail saat fitur diklik */}
      {modalOpen && selectedFeature && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 300, backgroundColor: 'white', border: '1px solid black', padding: 20, zIndex: 1000,
          boxShadow: '0 4px 8px rgba(0,0,0,0.2)', borderRadius: '8px'
        }}>
          <h3>Detail Fitur</h3>
          {Object.entries(selectedFeature).map(([key, value]) => (
            <p key={key}><strong>{key}:</strong> {String(value)}</p>
          ))}
          <button onClick={() => setModalOpen(false)} style={{ marginTop: '10px', padding: '8px 15px', cursor: 'pointer' }}>Tutup</button>
        </div>
      )}
    </div>
  );
}

export default MapPage;